import { router } from "@inertiajs/react"
import { EyeOff, Flag, MoreHorizontal, ShieldBan } from "lucide-react"
import { useState } from "react"

import { ReportDialog } from "@/components/report-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/hooks/use-auth"
import { contentHidesPath, userBlocksPath } from "@/routes"

interface ContentActionsMenuProps {
  contentType: string
  contentId: number
  authorId: number
  authorName: string
  onHide?: () => void
  className?: string
}

export function ContentActionsMenu({
  contentType,
  contentId,
  authorId,
  authorName,
  onHide,
  className,
}: ContentActionsMenuProps) {
  const { user, isAuthenticated } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false)

  if (!isAuthenticated || !user) return null
  if (authorId && Number(user.id) === Number(authorId)) return null

  const handleHide = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    router.post(
      contentHidesPath(),
      {
        hideable_type: contentType,
        hideable_id: contentId,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          onHide?.()
        },
      },
    )
  }

  const handleBlock = () => {
    router.post(
      userBlocksPath(),
      { blocked_id: authorId },
      {
        preserveScroll: true,
        onSuccess: () => {
          setBlockConfirmOpen(false)
        },
        onError: () => {
          setBlockConfirmOpen(false)
        },
      },
    )
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`text-muted-foreground hover:text-foreground h-8 w-8 p-0 ${className ?? ""}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Acciones</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(false)
              setReportOpen(true)
            }}
          >
            <Flag className="mr-2 h-4 w-4" />
            Reportar
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleHide}>
            <EyeOff className="mr-2 h-4 w-4" />
            Ocultar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(false)
              setBlockConfirmOpen(true)
            }}
            className="text-destructive focus:text-destructive"
          >
            <ShieldBan className="mr-2 h-4 w-4" />
            Bloquear a {authorName}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType={contentType}
        contentId={contentId}
      />

      <Dialog open={blockConfirmOpen} onOpenChange={setBlockConfirmOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Bloquear a {authorName}</DialogTitle>
            <DialogDescription>
              No veras mas contenido de este usuario. Puedes desbloquearlo en
              cualquier momento.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBlockConfirmOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleBlock}>
              Bloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
